import React, { useState, useRef, useEffect } from "react";
import { Input, Tooltip } from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import styled from "styled-components";

const TitleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TitleText = styled.span`
  cursor: pointer;
  font-weight: 600;
  @media (max-width: 768px) {
    cursor: pointer;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: block;
    width: 80%;
  }
  &:hover {
    opacity: 0.8;
  }
`;

const EditIcon = styled(EditOutlined)`
  cursor: pointer;
  color: ${({ theme }) => theme?.settings?.textColor || '#83899F'};
  font-size: 14px;
  transition: color 0.2s ease;
  &:hover {
    color: ${({ theme }) => theme?.settings?.primary || '#3D2969'};
  }
  @media (max-width: 768px) {
    position: absolute;
    left: 90%;
    top: 8px;
    width: 20px;
    height: 20px;
    z-index: 1;
  }
`;

const ActionIcon = styled.span`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  color: ${({ disabled, theme }) =>
    disabled
      ? theme?.settings?.textColor || "#ccc"
      : theme?.settings?.primary || "#3D2969"};
  font-size: 14px;
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  display: inline-flex;
  align-items: center;
  &:hover {
    color: ${({ disabled, theme }) =>
      disabled
        ? theme?.settings?.textColor || "#ccc"
        : theme?.settings?.blueDarkColor || "#5F42DE"};
  }
`;

const CancelIcon = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme?.settings?.textColor || "#83899F"};
  font-size: 14px;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  &:hover {
    color: ${({ theme }) => theme?.settings?.orangeColor || "#E65B5B"};
  }
`;

const StyledInput = styled(Input)`
  width: 250px;
  height: 28px !important;
  padding: 2px 8px !important;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  border: 1px solid ${({ theme }) => theme?.settings?.borderColorLight || "#f0f0f0"};
  border-radius: 6px !important;
  &:hover,
  &:focus {
    border-color: ${({ theme }) =>
      theme?.settings?.primary || '#3D2969'} !important;
  }
  @media (max-width: 768px) {
    width: 250px;
  }
  .ant-input {
    padding: 0 !important;
    @media (max-width: 768px) {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    }
  }
`;

const ActionsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MAX_LENGTH = 100;

const EditableTitle = ({
  value,
  onSave,
  placeholder = "Untitled",
  maxLength = MAX_LENGTH,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Update editValue when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || "");
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    setEditValue(value || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    // Don't save if empty
    if (!trimmedValue) {
      return;
    }

    // Don't save if unchanged
    if (trimmedValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save title:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (editValue.trim()) {
        handleSave();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = (e) => {
    // Don't save on blur if clicking on action buttons
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest(".editable-title-actions")) {
      return;
    }

    if (editValue.trim()) {
      handleSave();
    } else {
      handleCancel();
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    // Prevent typing beyond max length
    if (newValue.length <= maxLength) {
      setEditValue(newValue);
    }
  };

  const isValid = editValue.trim().length > 0;

  if (isEditing) {
    return (
      <TitleWrapper>
        <StyledInput
          ref={inputRef}
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={isSaving}
        />
        <ActionsWrapper className="editable-title-actions">
          <Tooltip title={!isValid ? "Title cannot be empty" : "Save"}>
            <ActionIcon
              disabled={!isValid || isSaving}
              onClick={() => isValid && !isSaving && handleSave()}
              tabIndex={0}
            >
              {isSaving ? <LoadingOutlined spin /> : <CheckOutlined />}
            </ActionIcon>
          </Tooltip>
          <Tooltip title="Cancel">
            <CancelIcon onClick={handleCancel} tabIndex={0}>
              <CloseOutlined />
            </CancelIcon>
          </Tooltip>
        </ActionsWrapper>
      </TitleWrapper>
    );
  }

  return (
    <TitleWrapper>
      <TitleText onClick={startEditing}>{value || placeholder}</TitleText>
      <Tooltip title="Edit title">
        <EditIcon onClick={startEditing} />
      </Tooltip>
    </TitleWrapper>
  );
};

export default EditableTitle;
